import { useFragment } from '@apollo/client/react';
import { CalendarIcon, HistoryIcon } from 'lucide-react';
import dayjs from 'dayjs';
import { Fragment } from 'react';

import { getProviderDisplayName } from 'src/utils/getProviderDisplayName';
import { renderLogo } from 'src/utils/renderLogo';
import { Badge } from '@/components/ui/badge';
import {
  CustomIntegrationFragment,
  OauthIntegrationFragment,
  ShareFileIntegrationFragment,
} from 'src/graphql/actions/integration/integration.fragment';
import { PROVIDER_KEY_TO_NAME } from 'src/shared/constants';

import type { IIntegrationQuery } from 'src/graphql/actions/integration/integration.query';

import IntegrationDropdown from './IntegrationDropdown';

const integrationTypeConfig = {
  CustomIntegration: {
    badge: 'Token',
    fragment: CustomIntegrationFragment,
  },
  OauthIntegration: {
    badge: 'OAuth',
    fragment: OauthIntegrationFragment,
  },
  ShareFileIntegration: {
    badge: 'Manual',
    fragment: ShareFileIntegrationFragment,
  },
};

type Props = {
  id: string;
  type: 'CustomIntegration' | 'OauthIntegration' | 'ShareFileIntegration';
};

const Header = ({ id, type }: Props) => {
  const { fragment, badge } = integrationTypeConfig[type as keyof typeof integrationTypeConfig];
  const { data: integration, complete } = useFragment<IIntegrationQuery['integration']>({
    fragment,
    from: {
      __typename: type,
      id,
    },
  });

  if (!complete) return null;

  const { provider, active, accountName, createdAt, updatedAt } = integration;

  const Icon = renderLogo(provider as keyof typeof PROVIDER_KEY_TO_NAME);

  return (
    <div className="p-6 pt-3">
      <div className="container mx-auto space-y-5">
        <div className="space-y-4">
          <div className="flex gap-2 items-center justify-between">
            <div className="flex items-start gap-1">
              <Icon className="size-9 shrink-0" />
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2.5">
                  <span className="font-medium">{accountName}</span>
                  {active ? (
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-800">Disconnected</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{getProviderDisplayName(provider)}</span>
                  <Badge variant="outline" className="px-2">
                    {badge}
                  </Badge>
                </div>
              </div>
            </div>
            <IntegrationDropdown provider={provider} __typename={type} id={id} />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <CalendarIcon className="size-4 shrink-0" />
              <span>Set up on {dayjs(createdAt).format('MMM DD, YYYY')}</span>
            </div>
            {type !== 'ShareFileIntegration' && (
              <Fragment>
                <span className="hidden sm:block">•</span>
                <div className="flex items-center gap-1">
                  <HistoryIcon className="size-4 shrink-0" />
                  <span>Updated on {dayjs(updatedAt).format('MMM DD, YYYY')}</span>
                </div>
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
